import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { DATASET_GROUP_TYPE_MERGED } from 'webapp/const/template-resolve';
import { OrderDirection, TemplateObjectType } from 'webapp/enums/enums';
import type { SearchProps } from 'webapp/utils/filter';

import type Board from 'webapp/models-custom/board';
import type BoardLoop from 'webapp/models-custom/board-loop';
import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';

export default class FilterSortBoardAccountTemplate extends FilterSort<Board | BoardLoop> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;

	@tracked override sortProp = 'order';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.ASC;

	override settingsNamePrefix = 'boardAccountTemplateFilterSettings';
	override trackingObjectName = 'boardAccountTemplate';

	override searchProps: SearchProps = {
		'templateRecord.title': 1,
		'templateRecord.description': 1,
	};

	override itemDomSelector = 'li.filter-sort-item';

	typeOptions: FilterOption<string>[] = [
		{ id: 'all', text: 'All' },
		{ id: TemplateObjectType.BOARD.toString(), text: 'Dashboards' }, // stringify TemplateObjectType enum to support default value in filter
		{ id: TemplateObjectType.BOARD_LOOP.toString(), text: 'Looped dashboards' }, // stringify TemplateObjectType enum to support default value in filter
	];

	constructor(options: FilterSortSettings<Board | BoardLoop>) {
		super(options);

		const selectedFilters = this.readSavedSelectedFilters();

		// Data source
		this.createFilter<string>({
			id: 2,
			label: 'Data source',
			name: 'data source',
			selected: selectedFilters[2] as string | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.datasourceOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((sourceTypeId) => {
					if (sourceTypeId === DATASET_GROUP_TYPE_MERGED) {
						return item.templateModel?.hasMergedDatasetMetrics === true;
					}
					return (item.templateModel?.specificSourceTypesKeys || []).includes(
						sourceTypeId,
					);
				});
			},
		});

		// Created by
		this.createFilter<number>({
			id: 3,
			label: 'Created by',
			name: 'creator',
			selected: selectedFilters[3] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.creatorOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((creatorId) => {
					if (creatorId === -1) {
						// exception: all others
						return !item.isMine;
					} else {
						return item.createdById === creatorId;
					}
				});
			},
		});

		// Type
		this.createFilter<string>({
			id: 4,
			label: 'Type',
			name: 'type',
			selected: selectedFilters[4] as string | undefined,
			stringValues: true,
			isMultiSelect: true,
			getOptions: () => {
				return this.typeOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((typeId) => {
					return typeId === 'all' || item.templateModel?.objectType.toString() === typeId; // stringify TemplateObjectType enum to support default value in filter
				});
			},
		});

		// Tags (used only)
		this.createFilter<number>({
			id: 1,
			label: 'Tags',
			name: 'tag',
			selected: selectedFilters[1] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.tagOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((tagId) => {
					return item.tags.includes(tagId);
				});
			},
		});
	}

	get tagOptions(): FilterOption<number>[] {
		const arrayCopy = Array.from(
			this.mmBoardsService.assignedTagsForAccountTemplates,
		) as FilterOption<number>[];
		if (arrayCopy.length) {
			arrayCopy.unshift({ id: 0, text: 'All' });
		}
		return arrayCopy;
	}

	@cached
	get datasourceOptions(): FilterOption<string>[] {
		// get how many boards each source is used on
		const sourceTypeArray: string[] = [];
		let hasMerged = false;

		this.allItems.forEach((board) => {
			(board.templateModel?.specificSourceTypesKeys || []).forEach((id: string) => {
				sourceTypeArray.push(id);
			});
			if (board.templateModel?.hasMergedDatasetMetrics) {
				hasMerged = true;
			}
		});

		const list = this.mmSourceTypesService.getSourceTypesListWithCount(sourceTypeArray);

		if (hasMerged) {
			list.push({
				id: DATASET_GROUP_TYPE_MERGED,
				text: 'Merged dataset',
				img: 'https://cdn1.databox.com/images/apps/s/Merged.png',
			});
		}

		list.unshift({ id: 'all', text: 'All' });

		return list;
	}

	@cached
	get creatorOptions(): FilterOption<number>[] {
		const allItemCountByCreator: Record<number, number> = {};
		this.allItems.forEach((item) => {
			const createdById = item.createdById;
			if (!allItemCountByCreator[createdById]) {
				allItemCountByCreator[createdById] = 0;
			}
			allItemCountByCreator[createdById]++;
		});

		return this.mmUsersService.getFilterOptionsForUsers(allItemCountByCreator);
	}
}
